import axios from "../config/axios";
import { QueryClient } from "react-query";
import { ApiSectionGroup, ApiFragmentNode, ApiConnection } from "../shared/interfaces";
import { PrimeDebugLog } from "../util/debugLog";

/**
 * Parameters for resolving a section group ID
 */
export interface ResolveParams {
    docId?: string;
    sectionGroupId?: string;
    fileId?: string;
    connectionId?: string;
    fragmentNodeId?: string;
}

/**
 * Resolved section group information
 */
export interface ResolvedIds {
    sectionGroupId: string;
    fileId?: string;
    documentId?: string;
    /** Full resolved object; absent when only the input sectionGroupId was echoed back. */
    sectionGroup?: ApiSectionGroup;
    /** The resolved connection when resolving by `connectionId`; `dstUuid` is absent when hidden. */
    connection?: ApiConnection;
}

export type ResolveFailureReason = "not_found" | "request_failed";

/**
 * API response format (snake_case from Python backend)
 *
 * Now returns full objects instead of just IDs, eliminating additional API calls.
 */
interface ApiResolveResponse {
    // Full objects
    section_group: ApiSectionGroup;
    fragment_node?: ApiFragmentNode;
    connection?: ApiConnection;
    // Backward compatibility: IDs at top level
    section_group_id: string;
    file_id?: string;
}

/**
 * Resolve section group ID from various inputs.
 *
 * Strategy:
 * 1. If section group ID already provided, return it immediately
 * 2. If file ID provided, try to find in local cache first
 * 3. If not in cache or other params provided, call server resolver API
 * 4. If queryClient provided, automatically populate React Query cache with response data
 *
 * @param params - Resolution parameters (at least one must be provided)
 * @param cache - Optional local cache of section groups to check before API call
 * @param queryClient - Optional React Query client to populate cache with resolved data
 * @param onFailure - Optional classifier for callers that need user-facing error copy
 * @returns Resolved section group and file IDs, or null if resolution failed
 */
export async function resolve(
    params: ResolveParams,
    cache?: ApiSectionGroup[] | null,
    queryClient?: QueryClient,
    onFailure?: (reason: ResolveFailureReason) => void,
): Promise<ResolvedIds | null> {
    // If section group ID already provided, return it immediately
    if (params.sectionGroupId) {
        return {
            sectionGroupId: params.sectionGroupId,
            fileId: params.fileId,
        };
    }

    // Try cache first (if file ID provided and cache available)
    if (params.fileId && cache) {
        const cachedSg = cache.find((sg) => sg.file?.id === params.fileId);
        if (cachedSg) {
            PrimeDebugLog("[resolver]", "Cache hit:", {
                fileId: params.fileId,
                sectionGroupId: cachedSg.id,
            });
            return {
                sectionGroupId: cachedSg.id,
                fileId: params.fileId,
                documentId: cachedSg.document_id,
                sectionGroup: cachedSg,
            };
        }
        PrimeDebugLog("[resolver]", "Cache miss for file ID:", params.fileId);
    }

    // Cache miss or using other resolution methods - call server API
    try {
        PrimeDebugLog("[resolver]", "Calling server API with params:", params);

        const response = await axios.get<ApiResolveResponse>("/api/ai/resolve", {
            params: {
                doc_id: params.docId,
                section_group_id: params.sectionGroupId,
                file_id: params.fileId,
                connection_id: params.connectionId,
                fragment_node_id: params.fragmentNodeId,
            },
        });

        PrimeDebugLog("[resolver]", "Server resolved:", response.data);

        // Populate React Query cache with the section group data we just received
        // This eliminates the need for a separate prefetch call
        if (queryClient && response.data.section_group) {
            queryClient.setQueryData(
                ["section-group", response.data.section_group_id],
                response.data.section_group,
            );
            PrimeDebugLog("[resolver]", "Populated React Query cache for section group:", {
                sectionGroupId: response.data.section_group_id,
            });
        }

        // Transform snake_case API response to camelCase
        // Fall back to local cache for document_id when section_group is absent
        const documentId =
            response.data.section_group?.document_id ??
            cache?.find((sg) => sg.id === response.data.section_group_id)?.document_id;
        return {
            sectionGroupId: response.data.section_group_id,
            fileId: response.data.file_id,
            documentId,
            sectionGroup: response.data.section_group,
            connection: response.data.connection,
        };
    } catch (error) {
        PrimeDebugLog("[resolver]", "Failed to resolve section group:", {
            params,
            error,
        });
        const status = (error as { response?: { status?: number } })?.response?.status;
        onFailure?.(status === 404 ? "not_found" : "request_failed");
        return null;
    }
}

/** Cap on ids per resolveBatch request — mirrors ai_engine's RESOLVE_BATCH_MAX_IDS. */
export const RESOLVE_BATCH_LIMIT = 200;

interface ApiResolvedRef {
    section_group_id: string;
    file_id?: string | null;
    document_id?: string | null;
}

interface ApiResolveBatchResponse {
    fragment_nodes: Record<string, ApiResolvedRef | null>;
    files: Record<string, ApiResolvedRef | null>;
}

export interface ResolveBatchResult {
    fragmentNodes: Record<string, ResolvedIds | null>;
    files: Record<string, ResolvedIds | null>;
}

function toResolvedIds(ref: ApiResolvedRef | null): ResolvedIds | null {
    if (!ref) return null;
    return {
        sectionGroupId: ref.section_group_id,
        fileId: ref.file_id ?? undefined,
        documentId: ref.document_id ?? undefined,
    };
}

/**
 * Resolve many fragment-node / file ids in one round-trip.
 *
 * Misses come back as `null` entries — the server never 404s per id, because
 * stale references are expected at scale (excerpt files, fragment nodes
 * superseded by re-ingests). Hits carry only the ids link builders consume;
 * callers that need the full section group use `resolve()`.
 */
export async function resolveBatch(
    fragmentNodeIds: string[],
    fileIds: string[],
): Promise<ResolveBatchResult> {
    const response = await axios.post<ApiResolveBatchResponse>("/api/ai/resolve/batch", {
        fragment_node_ids: fragmentNodeIds,
        file_ids: fileIds,
    });
    const fragmentNodes: Record<string, ResolvedIds | null> = {};
    for (const [id, ref] of Object.entries(response.data.fragment_nodes)) {
        fragmentNodes[id] = toResolvedIds(ref);
    }
    const files: Record<string, ResolvedIds | null> = {};
    for (const [id, ref] of Object.entries(response.data.files)) {
        files[id] = toResolvedIds(ref);
    }
    return { fragmentNodes, files };
}
